/**
 * ⟦C57 · A-174⟧ **La règle « moins de texte », écrite une fois.**
 *
 * Matt, 02/10/2026 : *« Il y a trop de trucs écrits partout, on ne lit plus rien à la fin. Il faut
 * laisser des boutons pour voir "les dessous", dans des détails de journal, mais tout doit être
 * simplifié : ne mettre que les mots ou petites phrases courtes importantes. »*
 *
 * Par défaut, un écran porte : un titre court, les chiffres qui comptent, et au plus UNE phrase
 * courte par bloc. Tout le reste passe derrière le bouton « Les dessous » (`components/
 * LesDessous.tsx`), qui ouvre un dialogue maison dans le style du Journal. **Aucune information
 * n'est supprimée : elle change de place.** Les textes longs restent dans `content/` ; quand un
 * texte court manque, il s'ajoute à côté du long dans un champ dédié (`titre_court`,
 * `chapo_court`…), jamais en réécrivant le long.
 *
 * Ce module ne porte que la mesure : les seuils de la règle et le compteur de mots, pour que les
 * tests (et les scripts de capture) jugent un texte avec le même mètre que l'écran. Pur : aucun
 * DOM, aucun store.
 */

/** Un titre à l'écran : 5 mots au plus. */
export const MOTS_TITRE_MAX = 5;
/** Une phrase à l'écran : 12 mots au plus, une seule par bloc. */
export const MOTS_PHRASE_MAX = 12;
/** Un choix d'événement : 6 mots au plus. */
export const MOTS_CHOIX_MAX = 6;

/**
 * Un « mot » : un jeton de texte qui contient au moins une lettre ou un chiffre. Les emojis et la
 * ponctuation isolée (le tiret long, le point médian) ne comptent pas ; « 1 245 » (deux groupes de chiffres séparés
 * par une espace insécable) ne fait qu'un mot. C'est le mètre de la mesure sur le DOM
 * (`assets/making-of/2026-10-02-C57-texte/scripts/mesure.cjs`), recopié ici à l'identique.
 */
const JETON = /[^\s  ]+(?:[  ]\d+)*/gu;
const A_UN_MOT = /[\p{L}\p{N}]/u;

export function compterMots(texte: string): number {
  let n = 0;
  for (const jeton of texte.matchAll(JETON)) {
    if (A_UN_MOT.test(jeton[0])) n += 1;
  }
  return n;
}

/**
 * Les phrases d'un bloc : coupées après `.` `!` `?` `…` ou `»` suivis d'une espace. Un libellé sans
 * ponctuation compte pour UNE phrase (la même coupe que la mesure sur le DOM).
 */
export function phrasesDe(texte: string): string[] {
  return texte.split(/(?<=[.!?…»])\s+/u).filter((p) => A_UN_MOT.test(p));
}

/** La plus longue phrase d'un bloc, en mots. */
export function plusLonguePhrase(texte: string): number {
  return phrasesDe(texte).reduce((max, p) => Math.max(max, compterMots(p)), 0);
}

/** `true` si le bloc tient dans la règle : une seule phrase, 12 mots au plus. */
export function estUnePhraseCourte(texte: string): boolean {
  const phrases = phrasesDe(texte);
  return phrases.length <= 1 && phrases.every((p) => compterMots(p) <= MOTS_PHRASE_MAX);
}

/** `true` si le titre tient dans la règle : 5 mots au plus. */
export function estUnTitreCourt(texte: string): boolean {
  return compterMots(texte) <= MOTS_TITRE_MAX;
}
